<!--
  AccountPasskeys.svelte — Passkey (WebAuthn) management page.

  Allows users to register new passkeys, rename them, delete them,
  and see when each was last used.  Uses the WebAuthn browser API
  via @simplewebauthn/browser for credential creation/assertion.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { Button, Spinner, Badge, Input, Modal } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import RelativeTime from '../../components/RelativeTime.svelte';
	import { ConfirmButton } from '../../components';
	import {
		listPasskeys,
		passkeyRegisterOptions,
		passkeyRegisterVerify,
		updatePasskeyName,
		deletePasskey
	} from '../../AppNetwork';
	import { toastSuccess, toastError } from '../../toast';
	import type { Passkey } from '../../AppTypes';
	import { startRegistration } from '@simplewebauthn/browser';
	import { t, t2 } from '../../i18n';

	/** All loaded passkeys. */
	let passkeys: Passkey[] = $state([]);
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Error banner text. */
	let error = $state('');

	/** Whether passkey registration is in progress. */
	let registering = $state(false);
	/** Show the name-entry modal after successful registration. */
	let showNameModal = $state(false);
	/** Friendly name input. */
	let friendlyName = $state('');

	/** Map of passkey IDs currently being renamed. */
	let renamingIds: Set<number> = $state(new Set());
	/** Map of passkey IDs currently being deleted. */
	let deletingIds: Set<number> = $state(new Set());

	/** ID of the passkey being inline-renamed, or null. */
	let editingId: number | null = $state(null);
	/** Current value of the inline rename input. */
	let editName = $state('');

	/** Fetch passkeys from the API. */
	async function loadPasskeys(): Promise<void> {
		loading = true;
		error = '';
		/** Attempt to fetch passkeys and surface any failure in the error banner. */
		try {
			passkeys = await listPasskeys();
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load passkeys');
		} finally {
			loading = false;
		}
	}

	onMount(loadPasskeys);

	/**
	 * Start the passkey registration ceremony.
	 * 1. Get options from server
	 * 2. Prompt user via browser WebAuthn API
	 * 3. Send attestation to server for verification
	 */
	async function handleRegister(): Promise<void> {
		registering = true;
		error = '';
		/** Run the full WebAuthn registration ceremony with server round-trips. */
		try {
			/* Step 1: Get creation options from server. */
			const { options, challenge } = await passkeyRegisterOptions();

			/* Step 2: Prompt the user via browser API. */
			const attResp = await startRegistration({ optionsJSON: options as any });

			/* Step 3: Ask for a friendly name. */
			friendlyName = '';
			showNameModal = true;

			/* Wait for the user to confirm the name (or use default). */
			const name = await waitForName();

			/* Step 4: Verify on the server. */
			const passkey = await passkeyRegisterVerify(challenge, attResp, name || undefined);
			passkeys = [passkey, ...passkeys];
			toastSuccess(t2('Passkey registered successfully!'));
		} catch (e: any) {
			/* User cancelled the WebAuthn dialog — not an error. */
			if (e?.name === 'NotAllowedError') {
				registering = false;
				showNameModal = false;
				return;
			}
			error = e instanceof Error ? e.message : t2('Failed to register passkey');
			showNameModal = false;
		} finally {
			registering = false;
		}
	}

	/** Awaits the name modal confirmation. Resolves with the entered name. */
	let resolveNamePromise: ((_name: string) => void) | null = null;

	/**
	 *
	 */
	function waitForName(): Promise<string> {
		return new Promise((resolve) => {
			resolveNamePromise = resolve;
		});
	}

	/**
	 *
	 */
	function confirmName(): void {
		if (resolveNamePromise) {
			resolveNamePromise(friendlyName.trim());
			resolveNamePromise = null;
		}
		showNameModal = false;
	}

	/** Start inline rename of a passkey. */
	function startRename(passkey: Passkey): void {
		editingId = passkey.id;
		editName = passkey.friendlyName;
	}

	/** Save the inline rename. */
	async function saveRename(id: number): Promise<void> {
		const name = editName.trim();
		/** Abort early if the user left the name blank. */
		if (!name) return;

		renamingIds.add(id);
		renamingIds = renamingIds;
		/** Persist the new name on the server and update the local list. */
		try {
			const updated = await updatePasskeyName(id, name);
			passkeys = passkeys.map((p) => (p.id === id ? { ...p, friendlyName: updated.friendlyName } : p));
			editingId = null;
			toastSuccess(t2('Passkey renamed.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to rename passkey'));
		} finally {
			renamingIds.delete(id);
			renamingIds = renamingIds;
		}
	}

	/** Cancel inline rename. */
	function cancelRename(): void {
		editingId = null;
	}

	/** Delete a passkey. */
	async function handleDelete(id: number): Promise<void> {
		deletingIds.add(id);
		deletingIds = deletingIds;
		/** Remove the passkey on the server and drop it from the local list. */
		try {
			await deletePasskey(id);
			passkeys = passkeys.filter((p) => p.id !== id);
			toastSuccess(t2('Passkey deleted.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete passkey'));
		} finally {
			deletingIds.delete(id);
			deletingIds = deletingIds;
		}
	}

	/** Handle Enter key in the rename input. */
	function handleRenameKeydown(event: KeyboardEvent, id: number): void {
		if (event.key === 'Enter') saveRename(id);
		if (event.key === 'Escape') cancelRename();
	}

	/** Handle Enter key in the name modal. */
	function handleNameKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter') confirmName();
	}
</script>

<div class="space-y-6">
	<div class="flex items-center justify-between">
		<div>
			<h3 class="text-xl font-semibold text-gray-900 dark:text-white">{$t("Passkeys")}</h3>
			<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
				{$t("Sign in securely with fingerprint, face recognition, or security keys.")}
			</p>
		</div>

		<Button size="sm" disabled={registering} onclick={handleRegister}>
			{#if registering}
				<Spinner size="4" class="mr-2" />
			{/if}
			<i class="fa-solid fa-plus mr-2"></i>
			{$t("Add passkey")}
		</Button>
	</div>

	{#if error}
		<AbstractCard class="max-w-none">
			<p class="text-red-500">{error}</p>
		</AbstractCard>
	{/if}

	{#if loading}
		<div class="flex justify-center py-12">
			<Spinner size="8" />
		</div>
	{:else if passkeys.length === 0 && !error}
		<AbstractCard class="max-w-none">
			<div class="py-8 text-center">
				<i class="fa-solid fa-fingerprint mb-3 text-4xl text-gray-300 dark:text-gray-600"></i>
				<p class="text-gray-500 dark:text-gray-400">{$t("No passkeys registered yet.")}</p>
				<p class="mt-1 text-sm text-gray-400 dark:text-gray-500">
					{$t("Add a passkey to sign in without a password.")}
				</p>
			</div>
		</AbstractCard>
	{:else}
		<div class="space-y-3">
			{#each passkeys as passkey (passkey.id)}
				<AbstractCard class="max-w-none">
					<div class="flex items-center justify-between gap-4">
						<div class="flex min-w-0 items-center gap-4">
							<!-- Passkey icon -->
							<div
								class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/20"
							>
								<i class="fa-solid fa-key text-primary-600 dark:text-primary-400"></i>
							</div>

							<div class="min-w-0">
								{#if editingId === passkey.id}
									<!-- Inline rename -->
									<div class="flex items-center gap-2">
										<Input
											size="sm"
											bind:value={editName}
											onkeydown={(e) => handleRenameKeydown(e, passkey.id)}
											class="w-48"
										/>
										<Button
											size="xs"
											onclick={() => saveRename(passkey.id)}
											disabled={renamingIds.has(passkey.id)}
										>
											{#if renamingIds.has(passkey.id)}
												<Spinner size="4" />
											{:else}
												{$t("Save")}
											{/if}
										</Button>
										<Button size="xs" color="alternative" onclick={cancelRename}>{$t("Cancel")}</Button>
									</div>
								{:else}
									<div class="flex flex-wrap items-center gap-2">
										<span class="text-sm font-medium text-gray-900 dark:text-white">
											{passkey.friendlyName}
										</span>
										{#if passkey.backedUp}
											<Badge color="blue" class="text-xs">{$t("Synced")}</Badge>
										{/if}
									</div>
								{/if}
								<div class="mt-0.5 space-x-3 text-xs text-gray-500 dark:text-gray-400">
									<span>
										<i class="fa-solid fa-calendar mr-1"></i>{$t("Added")} <RelativeTime
											date={passkey.created}
										/>
									</span>
									{#if passkey.lastUsedAt}
										<span>
											<i class="fa-solid fa-clock mr-1"></i>{$t("Last used")} <RelativeTime
												date={passkey.lastUsedAt}
											/>
										</span>
									{:else}
										<span>
											<i class="fa-solid fa-clock mr-1"></i>{$t("Never used")}
										</span>
									{/if}
								</div>
							</div>
						</div>

						{#if editingId !== passkey.id}
							<div class="flex flex-shrink-0 gap-2">
								<Button size="xs" color="alternative" onclick={() => startRename(passkey)}>
									<i class="fa-solid fa-pen text-xs"></i>
								</Button>
								<ConfirmButton
									size="xs"
									color="red"
									outline
									disabled={deletingIds.has(passkey.id)}
									onconfirm={() => handleDelete(passkey.id)}
								>
									{#if deletingIds.has(passkey.id)}
										<Spinner size="4" />
									{:else}
										<i class="fa-solid fa-trash text-xs"></i>
									{/if}
								</ConfirmButton>
							</div>
						{/if}
					</div>
				</AbstractCard>
			{/each}
		</div>
	{/if}
</div>

<!-- Name modal for newly registered passkey -->
<Modal bind:open={showNameModal} size="sm" title={$t("Name your passkey")} autoclose={false} outsideclose={false}>
	<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
		{$t('Give your passkey a name to identify it later (e.g. "MacBook Touch ID").')}
	</p>
	<Input placeholder={$t("My Passkey")} bind:value={friendlyName} onkeydown={handleNameKeydown} />
	{#snippet footer()}
	
			<Button onclick={confirmName}>
				{friendlyName.trim() ? $t('Save') : $t('Skip')}
			</Button>
		
	{/snippet}
</Modal>
